import { useEffect, useState } from "react";
import { fetchResources, downloadResourceMasterExcel } from "../Api/ResourceList";
import { Button, Container, Table } from "react-bootstrap";
import { FaDownload, FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { FaPencilAlt } from "react-icons/fa";
import { FaTimes } from "react-icons/fa";
import { Base_URL } from "../url";
import axios from "axios";

// 🔹 Import all master dropdown APIs from same file
import {
  fetchRoles,
  fetchDepartments,
  fetchOfficeCodes,
} from "../Api/ResourceMaster";

const ResourceList = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🔹 Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [officeFilter, setOfficeFilter] = useState("all");

  // 🔹 Master data lists
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [offices, setOffices] = useState([]);

  const navigate = useNavigate();
  const { user } = useAuth();

  // === Load masters ===
  const loadRoles = async () => {
    try {
      const data = await fetchRoles();
      setRoles(data || []);
    } catch (error) {
      toast.error("Error fetching roles");
    }
  };

  const loadDepartments = async () => {
    try {
      const data = await fetchDepartments();
      setDepartments(data || []);
    } catch (error) {
      toast.error("Error fetching departments");
    }
  };

  const loadOffices = async () => {
    try {
      const data = await fetchOfficeCodes(user?.organisationId);
      setOffices(data || []);
    } catch (error) {
      toast.error("Error fetching office codes");
    }
  };

  // === Load resources ===
  const loadResources = async () => {
    try {
      const data = await fetchResources(user?.organisationId);
      setResources(data || []);
    } catch (error) {
      toast.error(`Error Fetching Resources: ${error.message}`);
    }
  };

  useEffect(() => {
    loadResources();
    loadRoles();
    loadDepartments();
    loadOffices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const response = await downloadResourceMasterExcel();
      if (!response.data) {
        throw new Error("No data received from server");
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Resource_Master_Data.xlsx");
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        link.parentNode.removeChild(link);
      }, 100);

      toast.success("Resource data downloaded successfully!");
    } catch (error) {
      console.error("Error downloading resource data:", error);
      toast.error(`Error downloading resource data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddResource = () => {
    navigate("/resource-master");
  };

  const handleInactive = async (id) => {
    try {
      const res = await axios.put(
        `${Base_URL}/Resource/UpdateActiveStauts/id?id=${id}`
      );
      if (res.status === 200) {
        toast.success(`Resource Made Inactive Successfully`);
        loadResources();
      } else {
        toast.error("Error Making Resource Inactive");
      }
    } catch (error) {
      toast.error("Error Making Resource Inactive");
    }
  };

  // === Apply filters: status + role + department + office ===
  const filteredResources = resources.filter((resource) => {
    const statusMatch =
      statusFilter === "all" ||
      (statusFilter === "active" && resource.active) ||
      (statusFilter === "inactive" && !resource.active);

    const roleMatch =
      roleFilter === "all" || resource.role === roleFilter;

    const departmentMatch =
      departmentFilter === "all" || resource.department === departmentFilter;

    const officeMatch =
      officeFilter === "all" || resource.officeName === officeFilter;

    return statusMatch && roleMatch && departmentMatch && officeMatch;
  });

  return (
    <div className="main-content expand">
      <Container fluid className="list-container">
        <div className="page-headings">
          <div className="heading-name">
            <h2 className="list-title">Resource List</h2>
          </div>

          <div className="heading-options flex gap-4">
            {/* Add Resource Button */}
            <button
              className="
                px-4 py-2 
                bg-gray-100 
                text-gray-800 
                border border-gray-300 
                rounded-xl 
                flex items-center gap-2
                hover:bg-gray-200 
                hover:border-gray-400
                transition-all duration-200
              "
              onClick={handleAddResource}
            >
              <FaPlus className="text-gray-700" />
              Add Resource
            </button>

            {/* Download Button */}
            <button
              className="
                px-4 py-2 
                bg-blue-600 
                text-white 
                rounded-xl 
                flex items-center gap-2
                hover:bg-blue-700
                transition-all duration-200
                disabled:bg-blue-300
              "
              onClick={handleDownloadExcel}
              disabled={loading}
            >
              {loading ? (
                "Downloading..."
              ) : (
                <>
                  <FaDownload className="text-white" />
                  Download Data
                </>
              )}
            </button>
          </div>
        </div>

        {/* Table ALWAYS rendered; only body content changes */}
        <div className="table-wrapper">
          <div className="table-scroll-container">
            <Table bordered hover className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Actions</th>

                  {/* Status with dropdown in header */}
                  <th>
                    Status <br />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={{
                        padding: "2px",
                        borderRadius: "6px",
                        fontSize: "0.8rem",
                        marginTop: "4px",
                      }}
                    >
                      <option value="all">All</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </th>

                  <th>Resource ID</th>
                  <th>Name</th>
                  <th>DOB</th>

                  {/* Role with dropdown in header */}
                  <th>
                    ROLE <br />
                    <select
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      style={{
                        padding: "2px",
                        borderRadius: "6px",
                        fontSize: "0.8rem",
                        marginTop: "4px",
                      }}
                    >
                      <option value="all">All</option>
                      {roles.map((role, index) => (
                        <option key={index} value={role.description}>
                          {role.description}
                        </option>
                      ))}
                    </select>
                  </th>

                  {/* Department with dropdown in header */}
                  <th>
                    Department <br />
                    <select
                      value={departmentFilter}
                      onChange={(e) => setDepartmentFilter(e.target.value)}
                      style={{
                        padding: "2px",
                        borderRadius: "6px",
                        fontSize: "0.8rem",
                        marginTop: "4px",
                      }}
                    >
                      <option value="all">All</option>
                      {departments.map((dept, index) => (
                        <option key={index} value={dept.description}>
                          {dept.description}
                        </option>
                      ))}
                    </select>
                  </th>

                  <th>Email ID</th>
                  <th>Company Name</th>

                  {/* Office Name with dropdown in header */}
                  <th>
                    Office Name <br />
                    <select
                      value={officeFilter}
                      onChange={(e) => setOfficeFilter(e.target.value)}
                      style={{
                        padding: "2px",
                        borderRadius: "6px",
                        fontSize: "0.8rem",
                        marginTop: "4px",
                      }}
                    >
                      <option value="all">All</option>
                      {offices.map((office, index) => (
                        <option key={index} value={office.description}>
                          {office.description}
                        </option>
                      ))}
                    </select>
                  </th>

                  <th>Employee Type</th>
                  <th>Fathers Name</th>
                  <th>Marital Status</th>
                  <th>Spouse Name</th>
                  <th>Phone No</th>
                  <th>PAN No</th>
                  <th>Aadhar No</th>
                  <th>Blood Group</th>
                  <th>UAN No</th>
                  <th>URL Aadhar Card</th>
                  <th>URL PAN Card</th>
                  <th>Emergency Contact Name</th>
                  <th>Emergency Contact Relation</th>
                  <th>Emergency Contact Phone No</th>
                  <th>Payment Group</th>
                  <th>Bank Name</th>
                  <th>Account No.</th>
                  <th>IFSC Code</th>
                  <th>Cost Center</th>
                </tr>
              </thead>

              <tbody>
                {filteredResources.length > 0 ? (
                  filteredResources.map((resource, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>

                      <td style={{ display: "flex", gap: "4px" }}>
                        <Button
                          variant="outline-primary"
                          title="Edit Resource"
                          onClick={() =>
                            navigate(`/updateResource/${resource.resourceId}`)
                          }
                        >
                          <FaPencilAlt />
                        </Button>
                        <Button
                          variant="outline-danger"
                          title="Make Resource Inactive"
                          disabled={resource.active === false}
                          onClick={() => handleInactive(resource?.resourceId)}
                        >
                          <FaTimes />
                        </Button>
                      </td>

                      <td
                        style={{
                          color: resource?.active ? "green" : "red",
                          fontWeight: 600,
                        }}
                      >
                        {resource?.active ? "Active" : "Inactive"}
                      </td>

                      <td>{resource.resourceId}</td>
                      <td>{resource.name}</td>
                      <td>{moment(resource.dob).format("L")}</td>
                      <td>{resource.role}</td>
                      <td>{resource.department}</td>
                      <td>{resource.emailId}</td>
                      <td>{resource.companyName}</td>
                      <td>{resource.officeName}</td>
                      <td>{resource.employeeType}</td>
                      <td>{resource.fathersName}</td>
                      <td>{resource.maritalStatus}</td>
                      <td>{resource.spouseName}</td>
                      <td>{resource.phoneNumber}</td>
                      <td>{resource.panNumber}</td>
                      <td>{resource.aadharNumber}</td>
                      <td>{resource.bloodGroup}</td>
                      <td>{resource.uanNumber}</td>
                      <td>{resource.urlAadharCard}</td>
                      <td>{resource.urlPanCard}</td>
                      <td>{resource.emergencyContactName}</td>
                      <td>{resource.emergencyContactRelation}</td>
                      <td>{resource.emergencyContactPhoneNumber}</td>
                      <td>{resource.paymentGroup}</td>
                      <td>{resource.bankName}</td>
                      <td>{resource.accountNo}</td>
                      <td>{resource.ifscCode}</td>
                      <td>{resource.costCenter}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    {/* 30 columns total → colspan=30 */}
                    <td
                      colSpan={30}
                      style={{
                        textAlign: "center",
                        fontWeight: 600,
                        padding: "16px",
                      }}
                    >
                      No resources found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ResourceList;
